/**
 * Presentational sub-components extracted from `pages/GamePlay.jsx` to keep that
 * file focused on game flow / state logic. All components in this file are pure
 * (no external state, no API calls) — they receive everything they need via
 * props and render UI only.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Timer as TimerIcon } from "lucide-react";
import {
  getCardSurfaceGradient,
  getCardShapeColors,
  getQuestionTextStyle,
  getOutlineFilter,
  getOutlineTextShadow,
} from "@/lib/gameVisual";
import { flagUrl, nationalityLabel } from "@/lib/nationalities";
import { AvatarSVG } from "@/lib/avatarBuilder";
import { useOutlinedImage, waitForOutline } from "@/lib/outlineRaster";
import FitText from "@/components/FitText";

export const SUPERT = ["S", "U", "P", "E", "R", "T"];
export const POSITIONS = "ABCDEFGHIJ".split("");
// Format-aware position pools. ORIGINAL = 10 positions (A-J) × 5 levels.
// QUIZ = 7 positions (A-G) × 4 levels. Used by RouletteCard / CardFront.
export const POSITIONS_BY_FORMAT = {
  original: "ABCDEFGHIJ".split(""),
  quiz: "ABCDEFG".split(""),
};
export const LEVELS_BY_FORMAT = {
  original: 5,
  quiz: 4,
};
// Visual override for QUIZ format — a warm yellow gradient instead of the
// classic deep-blue card surface. Applied to BoardSurface when format=quiz
// and the game hasn't set a custom `card_surface_color`.
export const QUIZ_CARD_SURFACE = "#facc15"; // amber-400

// Official SUPERT brand letter PNGs — used by the roulette big-reveal AND the
// mini thumbnails shown inside SupertBar progress slots.
// Served same-origin (frontend/public/game-letters/) so the outlineRaster
// canvas can read the pixels without CORS tainting.
export const BRAND_ASSETS = {
  S: "/game-letters/s.png",
  U: "/game-letters/u.png",
  P: "/game-letters/p.png",
  E: "/game-letters/brand-e.png",
  R: "/game-letters/r.png",
  T: "/game-letters/brand-t.png",
  "*": "/game-letters/star.png",
};

export const formatTime = (seconds) => {
  const total = Math.max(0, seconds);
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const cs = Math.floor((total - Math.floor(total)) * 100);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(cs).padStart(2, "0")}`;
};

// -------------- Card transition presets --------------
const CARD_TILT = -4; // degrees — roleta tilt only (2D z-axis)
const ROULETTE_TILT_Y = 10; // 3D perspective tilt — roleta card tilts so its LEFT edge recedes
const CARDFRONT_TILT_Y = -10; // 3D perspective tilt — question card tilts so its RIGHT edge recedes
// PERF: box-shadow (cheap, compositor-friendly) replaces the former
// `filter: drop-shadow(...)` which forced a full re-rasterization of the
// whole card subtree on every repaint.
const CARDFRONT_SHADOW = "12px 16px 22px rgba(15,23,42,0.30)";
const ROULETTE_SHADOW = "-12px 16px 22px rgba(15,23,42,0.30)";

const CARD_EFFECTS = {
  fade: {
    initial: { opacity: 0, scale: 0.95, rotateY: CARDFRONT_TILT_Y },
    animate: { opacity: 1, scale: 1, rotateY: CARDFRONT_TILT_Y },
    exit: { opacity: 0, scale: 0.95, rotateY: CARDFRONT_TILT_Y },
    transition: { duration: 0.15, ease: "easeOut" },
  },
  flip: {
    initial: { rotateY: 90, opacity: 0, scale: 0.9 },
    animate: { rotateY: CARDFRONT_TILT_Y, opacity: 1, scale: 1 },
    exit: { rotateY: -90, opacity: 0, scale: 0.9 },
    transition: { type: "spring", stiffness: 260, damping: 22 },
  },
  slide: {
    initial: { x: -80, opacity: 0, rotateY: CARDFRONT_TILT_Y },
    animate: { x: 0, opacity: 1, rotateY: CARDFRONT_TILT_Y },
    exit: { x: 80, opacity: 0, rotateY: CARDFRONT_TILT_Y },
    transition: { duration: 0.15, ease: "easeOut" },
  },
  zoom: {
    initial: { scale: 0.4, opacity: 0, rotateY: CARDFRONT_TILT_Y },
    animate: { scale: 1, opacity: 1, rotateY: CARDFRONT_TILT_Y },
    exit: { scale: 0.4, opacity: 0, rotateY: CARDFRONT_TILT_Y },
    transition: { type: "spring", stiffness: 320, damping: 24 },
  },
  none: {
    initial: { opacity: 1, rotateY: CARDFRONT_TILT_Y },
    animate: { opacity: 1, rotateY: CARDFRONT_TILT_Y },
    exit: { opacity: 1, rotateY: CARDFRONT_TILT_Y },
    transition: { duration: 0 },
  },
};

const ROULETTE_EFFECTS = {
  slide_left: {
    initial: { x: -80, opacity: 0, rotate: -12, rotateY: ROULETTE_TILT_Y },
    animate: { x: 0, opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    exit: { x: -80, opacity: 0, rotate: -12, rotateY: ROULETTE_TILT_Y },
    transition: { duration: 0.18, ease: "easeOut" },
  },
  slide_right: {
    initial: { x: 80, opacity: 0, rotate: 12, rotateY: ROULETTE_TILT_Y },
    animate: { x: 0, opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    exit: { x: 80, opacity: 0, rotate: 12, rotateY: ROULETTE_TILT_Y },
    transition: { duration: 0.18, ease: "easeOut" },
  },
  fade: {
    initial: { opacity: 0, scale: 0.9, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    animate: { opacity: 1, scale: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    exit: { opacity: 0, scale: 0.9, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    transition: { duration: 0.15 },
  },
  flip: {
    initial: { rotateY: -120, opacity: 0, scale: 0.85 },
    animate: { rotateY: ROULETTE_TILT_Y, opacity: 1, scale: 1, rotate: CARD_TILT },
    exit: { rotateY: 120, opacity: 0, scale: 0.85 },
    transition: { type: "spring", stiffness: 260, damping: 22 },
  },
  zoom: {
    initial: { scale: 0.4, opacity: 0, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    animate: { scale: 1, opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    exit: { scale: 0.4, opacity: 0, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    transition: { type: "spring", stiffness: 320, damping: 24 },
  },
  none: {
    initial: { opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    animate: { opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    exit: { opacity: 1, rotate: CARD_TILT, rotateY: ROULETTE_TILT_Y },
    transition: { duration: 0 },
  },
};

const getEffect = (key) => CARD_EFFECTS[key] || CARD_EFFECTS.fade;
const getRouletteEffect = (key) => ROULETTE_EFFECTS[key] || ROULETTE_EFFECTS.slide_left;

// -------------- Shared SUPERTMATIK board theme --------------
export function BoardSurface({ children, className = "", noShapes = false, borderColor = "#03060f", borderWidth = 22, surfaceColor = "#173b78" }) {
  return (
    <div
      className={`relative rounded-[8px] overflow-hidden ${className}`}
      style={{
        background: [
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, transparent 1px, transparent 5px)",
          getCardSurfaceGradient(surfaceColor),
        ].join(", "),
        boxShadow:
          `0 0 0 5px #1e3a78, 0 0 0 ${5 + Math.max(0, borderWidth)}px ${borderColor}, 0 18px 36px rgba(0,0,0,0.7), inset 0 0 0 2px rgba(255,255,255,0.08)`,
      }}
    >
      {!noShapes && (() => {
        const shapes = getCardShapeColors(surfaceColor);
        return (
          <>
            <div className="absolute pointer-events-none" style={{ left: "-6%", top: "-2%", width: "55%", height: "55%", background: shapes.shape1, clipPath: "polygon(0 0, 100% 18%, 78% 78%, 18% 100%, 0 70%)", opacity: 0.32 }} />
            <div className="absolute pointer-events-none" style={{ left: "10%", bottom: "-8%", width: "70%", height: "55%", background: shapes.shape2, clipPath: "polygon(35% 0, 100% 30%, 70% 100%, 8% 90%, 0 40%)", opacity: 0.28 }} />
            <div className="absolute pointer-events-none rounded-full" style={{ right: "8%", top: "12%", width: "16%", height: "16%", background: shapes.shape2, opacity: 0.18 }} />
            <div className="absolute pointer-events-none" style={{ right: "-4%", top: "45%", width: "22%", height: "12%", background: shapes.shape1, borderRadius: "50%", opacity: 0.15, transform: "rotate(-18deg)" }} />
            <div className="absolute pointer-events-none" style={{ left: "62%", top: "8%", width: "14%", height: "18%", background: shapes.shape1, clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)", opacity: 0.20, transform: "rotate(15deg)" }} />
            <div className="absolute pointer-events-none" style={{ right: "6%", bottom: "12%", width: "10%", height: "14%", background: shapes.shape2, clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)", opacity: 0.18, transform: "rotate(-22deg)" }} />
            <div className="absolute pointer-events-none rounded-full" style={{ left: "42%", top: "5%", width: "7%", height: "7%", background: shapes.shape1, opacity: 0.18 }} />
            <div className="absolute pointer-events-none rounded-full" style={{ left: "8%", top: "48%", width: "5%", height: "5%", background: shapes.shape2, opacity: 0.22 }} />
          </>
        );
      })()}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
}

// -------------- Yellow A-J letter (image + CSS fallback) --------------
const ANSWER_ASSETS = {
  A: "/game-letters/a.png",
  B: "/game-letters/b.png",
  C: "/game-letters/c.png",
  D: "/game-letters/d.png",
  E: "/game-letters/e.png",
  F: "/game-letters/f.png",
  G: "/game-letters/g.png",
  H: "/game-letters/h.png",
  I: "/game-letters/i.png",
  J: "/game-letters/j.png",
};

// PERF: pre-download + pre-rasterize every letter/star PNG with the EXACT
// outline filters gameplay will use, so the very first spin renders from the
// raster cache instantly (no image download + no synchronous canvas work on
// the first "playing" frame — the main cause of the slow/unclickable first
// game reported on cold cache). Call while the lobby / READY modal is showing.
// Downloads one PNG and marks it in `_readySrcs` so `useImageReady` callers
// render the final <img> from their very first frame.
function preloadImageReady(src) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => { _readySrcs.add(src); resolve(); };
    im.onerror = () => resolve();
    im.src = src;
  });
}

// CRITICAL warmup — only what the FIRST gameplay paint needs: every letter
// PNG downloaded + the 10 small A-J letters rasterized + the single reveal
// char the roulette will show first. GamePlay gates "INICIAR JOGO" on this
// (fast), while `warmupGameAssets` finishes the full set in the background.
export function warmupCriticalAssets(visual = {}, revealChar = "*") {
  try {
    const smallFilter = getOutlineFilter(visual?.small_letter_outline_color ?? "#000000", visual?.small_letter_outline_width ?? 0.5, visual?.small_letter_outline_mode ?? "solid", visual?.small_letter_outline_color2 ?? "#666666");
    const revealFilter = getOutlineFilter(visual?.reveal_outline_color ?? "#000000", visual?.reveal_outline_width ?? 0.5, visual?.reveal_outline_mode ?? "solid", visual?.reveal_outline_color2 ?? "#666666");
    const jobs = [];
    [...Object.values(ANSWER_ASSETS), ...Object.values(BRAND_ASSETS)].forEach((src) => jobs.push(preloadImageReady(src)));
    Object.values(ANSWER_ASSETS).forEach((src) => jobs.push(waitForOutline(src, smallFilter, 96)));
    const revealSrc = BRAND_ASSETS[revealChar] || BRAND_ASSETS["*"];
    jobs.push(waitForOutline(revealSrc, revealFilter, 320));
    if (visual?.card_logo_image) jobs.push(preloadImageReady(visual.card_logo_image));
    return Promise.all(jobs);
  } catch (e) {
    return Promise.resolve();
  }
}

// FULL warmup — everything gameplay may need across rounds (all reveal chars
// at 320px + SupertBar thumbs at 64px). Runs in the background; each job
// yields to the main thread between rasterizations.
export function warmupGameAssets(visual = {}) {
  try {
    const smallFilter = getOutlineFilter(visual?.small_letter_outline_color ?? "#000000", visual?.small_letter_outline_width ?? 0.5, visual?.small_letter_outline_mode ?? "solid", visual?.small_letter_outline_color2 ?? "#666666");
    const revealFilter = getOutlineFilter(visual?.reveal_outline_color ?? "#000000", visual?.reveal_outline_width ?? 0.5, visual?.reveal_outline_mode ?? "solid", visual?.reveal_outline_color2 ?? "#666666");
    const barFilter = getOutlineFilter("#000000", 0, "solid", "#666666");
    const jobs = [];
    const track = (src, p) => jobs.push(p.then(() => { _readySrcs.add(src); }));
    Object.values(ANSWER_ASSETS).forEach((src) => track(src, waitForOutline(src, smallFilter, 96)));
    Object.values(BRAND_ASSETS).forEach((src) => {
      track(src, waitForOutline(src, revealFilter, 320));
      track(src, waitForOutline(src, barFilter, 64));
    });
    if (visual?.card_logo_image) {
      jobs.push(preloadImageReady(visual.card_logo_image));
    }
    return Promise.all(jobs);
  } catch (e) {
    return Promise.resolve();
  }
}

// PERF: renders a pre-rasterized (image+outline) PNG instead of applying the
// expensive multi-pass CSS drop-shadow chain live. Falls back to the original
// CSS filter while rasterizing (first ~100ms) or on old Safari.
function OutlinedImage({ src, filter, nominal = 64, alt = "", className = "", style = {}, draggable = false, onError, testId }) {
  const entry = useOutlinedImage(src, filter, nominal);
  const { transform, ...rest } = style || {};
  if (entry && entry.url) {
    const t = `${transform || ""} scale(${entry.scale.toFixed(4)})`.trim();
    return (
      <img
        src={entry.url}
        alt={alt}
        draggable={draggable}
        className={className}
        onError={onError}
        data-testid={testId}
        style={{ ...rest, transform: t }}
      />
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      draggable={draggable}
      className={className}
      onError={onError}
      data-testid={testId}
      style={{ ...rest, transform, filter }}
    />
  );
}

// Tracks which letter PNGs have finished downloading. While a PNG is still
// in flight (first site visit, cold cache) callers render a styled CSS text
// fallback instead of an empty <img>, so letters are ALWAYS visible.
const _readySrcs = new Set();
function _syncCheck(src) {
  if (_readySrcs.has(src)) return true;
  const img = new Image();
  img.src = src;
  if (img.complete && img.naturalWidth > 0) { _readySrcs.add(src); return true; }
  return false;
}
function useImageReady(src) {
  const [ready, setReady] = useState(() => !!src && _syncCheck(src));
  useEffect(() => {
    if (!src) { setReady(false); return undefined; }
    if (_syncCheck(src)) { setReady(true); return undefined; }
    setReady(false);
    let on = true;
    const img = new Image();
    img.onload = () => { _readySrcs.add(src); if (on) setReady(true); };
    img.src = src;
    return () => { on = false; };
  }, [src]);
  return ready;
}

export function YellowLetter({ char, size = "md", dim = false, outlineColor, outlineWidth, outlineMode, outlineColor2, sizePx = null }) {
  // `sizePx` (optional) — when set, overrides the `size` preset with an
  // explicit pixel value. Used by the mini-roulette position letter so
  // the admin can fine-tune it via /admin/jogos → Visual.
  const preset = {
    xs: { w: "20px", md: "26px" },
    sm: { w: "28px", md: "36px" },
    md: { w: "36px", md: "48px" },
    lg: { w: "56px", md: "72px" },
  }[size] || { w: "36px", md: "48px" };
  const sizePxObj = sizePx ? { w: `${sizePx}px`, md: `${sizePx}px` } : preset;

  const imgUrl = ANSWER_ASSETS[(char || "").toUpperCase()];
  const imgReady = useImageReady(imgUrl);
  const filter = getOutlineFilter(outlineColor ?? "#000000", outlineWidth ?? 0.5, outlineMode ?? "solid", outlineColor2 ?? "#666666");
  if (imgUrl && imgReady) {
    return (
      <OutlinedImage
        src={imgUrl}
        filter={filter}
        nominal={96}
        alt={char}
        className={`object-contain select-none ${dim ? "opacity-60" : ""}`}
        style={{
          width: `min(${sizePxObj.md}, ${sizePxObj.w})`,
          height: `min(${sizePxObj.md}, ${sizePxObj.w})`,
          transform: "skewX(-12deg)",
          transformOrigin: "center center",
        }}
      />
    );
  }
  const sizeCls = {
    xs: "text-base md:text-lg",
    sm: "text-xl md:text-2xl",
    md: "text-2xl md:text-3xl",
    lg: "text-4xl md:text-5xl",
  }[size] || "text-2xl md:text-3xl";
  const spanStyle = sizePx ? { fontSize: `${sizePx}px` } : undefined;
  return (
    <span
      className={`font-[Fredoka] font-extrabold ${sizePx ? "" : sizeCls} ${dim ? "text-amber-300/60" : "text-amber-400"}`}
      style={{
        ...(spanStyle || {}),
        textShadow: [
          "-1.5px -1.5px 0 #000", "1.5px -1.5px 0 #000", "-1.5px 1.5px 0 #000", "1.5px 1.5px 0 #000",
          "0 -1.5px 0 #000", "0 1.5px 0 #000", "-1.5px 0 0 #000", "1.5px 0 0 #000",
          "2px 3px 0 rgba(0,0,0,0.55)",
          "0 4px 6px rgba(0,0,0,0.35)",
        ].join(", "),
        lineHeight: 1,
      }}
    >
      {char}
    </span>
  );
}

function SupermatikBrand({ logo, size, filter }) {
  if (!logo) return null;
  return (
    <img
      src={logo}
      alt="supertmatik"
      className="object-contain drop-shadow-[1px_1px_0_rgba(0,0,0,0.5)]"
      style={size ? { height: `${size}px`, filter } : { height: "28px", filter }}
      data-testid="card-logo-image"
    />
  );
}

// -------------- Roulette card (left mini-grid + history strip + reveal) --------------
// PERF: RouletteCard is memoized because the GamePlay parent re-renders at
// ~60 Hz driven by the timer state (`time` from useGameTimer). Without memo,
// this heavy subtree (all cells, letters, SVG filters, brand-logo) would
// re-render on every timer frame — which was the main cause of jumpy time
// counting and slow round chaining reported by the user.
const _RouletteCard = function RouletteCard({ quadriculas, reveal, playerLevel, round, spinning, effect, displayedByPos = {}, snippetsByPos = {}, cardLogo, cardLogoCfg = { height: 44, offsetX: -10, offsetY: 10 }, visual, lang = "pt", activePosition = null, format = "original", initialIdleChar = null }) {
  const fmtNorm = format === "quiz" ? "quiz" : "original";
  const positions = POSITIONS_BY_FORMAT[fmtNorm];
  const levelsCount = LEVELS_BY_FORMAT[fmtNorm];
  // Slice INSIDE the memoized component so callers can pass a stable ref
  // (React.memo shallow compare on `quadriculas` array).
  const quads = Array.isArray(quadriculas) ? quadriculas.slice(0, levelsCount) : [];
  // For QUIZ (7 letters), use 1×7 layout (single tall column — long cells fit
  // the question text); ORIGINAL keeps 2×5.
  const leftGridClass = fmtNorm === "quiz"
    ? "grid grid-cols-1 grid-rows-7 gap-1 md:gap-1.5"
    : "grid grid-cols-2 grid-rows-5 grid-flow-col gap-1 md:gap-1.5";
  const topStripClass = fmtNorm === "quiz"
    ? "grid grid-cols-4 gap-0.5 md:gap-1"
    : "grid grid-cols-5 gap-0.5 md:gap-1";
  const surfaceColor = fmtNorm === "quiz"
    ? (visual?.card_surface_color && visual.card_surface_color !== "#173b78"
        ? visual.card_surface_color
        : QUIZ_CARD_SURFACE)
    : (visual?.card_surface_color || "#173b78");
  const fx = getRouletteEffect(effect);
  const borderColor = visual?.roulette_border_color || "#03060f";
  const borderWidth = visual?.roulette_border_width ?? 22;

  const labelEnabled = visual?.roulette_label_enabled !== false;
  const labelText = (() => {
    if (lang === "en") return visual?.roulette_label_text_en || "SELECTOR CARD";
    if (lang === "es") return visual?.roulette_label_text_es || "CARTA RULETA";
    return visual?.roulette_label_text_pt || "CARTA ROLETA";
  })();

  const IDLE_CHARS = ["S", "U", "P", "E", "R", "T", "*"];
  const [idleChar, setIdleChar] = useState(() => (initialIdleChar && IDLE_CHARS.includes(initialIdleChar)) ? initialIdleChar : IDLE_CHARS[Math.floor(Math.random() * IDLE_CHARS.length)]);
  const [cardNum, setCardNum] = useState(() => Math.floor(Math.random() * 54) + 1);
  // Re-roll the idle letter/card-number ONLY when the round value actually
  // changes — never on mount (nor on StrictMode's double effect run). A
  // mount-time re-roll made the big reveal letter visibly swap right after
  // "INICIAR JOGO".
  const prevRoundRef = useRef(round);
  useEffect(() => {
    if (prevRoundRef.current === round) return;
    prevRoundRef.current = round;
    setIdleChar((prev) => {
      let next = prev;
      while (next === prev) {
        next = IDLE_CHARS[Math.floor(Math.random() * IDLE_CHARS.length)];
      }
      return next;
    });
    setCardNum((prev) => {
      let n = prev;
      while (n === prev) {
        n = Math.floor(Math.random() * 54) + 1;
      }
      return n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const displayChar = reveal || idleChar;
  // Cold-cache fallback: while the brand PNG for the reveal is still
  // downloading, render the CSS text letter/star instead of a blank card.
  const revealImgReady = useImageReady(BRAND_ASSETS[displayChar]);
  // Per-game configurable 3D + 2D tilts — defaults preserve the convergent look.
  const tiltY = Number.isFinite(visual?.roulette_tilt_y) ? Number(visual.roulette_tilt_y) : ROULETTE_TILT_Y;
  const tiltX = Number.isFinite(visual?.roulette_tilt_x) ? Number(visual.roulette_tilt_x) : 0;
  const tiltZ = Number.isFinite(visual?.roulette_tilt_z) ? Number(visual.roulette_tilt_z) : CARD_TILT;
  const persp = Math.max(400, Math.min(4000, Number(visual?.perspective_strength) || 1400));
  const showShadow = visual?.card_depth_shadow !== false;
  const wrapScale = Math.max(0.5, Math.min(2, Number(visual?.roulette_scale) || 1));
  const wrapOffsetX = Number.isFinite(visual?.roulette_offset_x) ? Number(visual.roulette_offset_x) : 0;
  const wrapOffsetY = Number.isFinite(visual?.roulette_offset_y) ? Number(visual.roulette_offset_y) : 0;
  const tiltedFx = {
    initial: { ...fx.initial, rotateY: fx.initial?.rotateY ?? tiltY, rotateX: tiltX, rotate: tiltZ },
    animate: { ...fx.animate, rotateY: tiltY, rotateX: tiltX, rotate: tiltZ },
    exit: { ...fx.exit, rotateY: fx.exit?.rotateY ?? tiltY, rotateX: tiltX, rotate: tiltZ },
    transition: fx.transition,
  };
  return (
    <div
      className="relative w-full max-w-[340px] aspect-[4/3] supert-card-tilt-left"
      style={{
        perspective: `${persp}px`,
        perspectiveOrigin: "70% 50%",
        transform: `translate(${wrapOffsetX}px, ${wrapOffsetY}px) scale(${wrapScale})`,
        transformOrigin: "center center",
      }}
    >
      {labelEnabled && labelText && (
        <div
          data-testid="roulette-label"
          className="absolute z-20 pointer-events-none select-none"
          style={{
            top: 0,
            left: "50%",
            transform: `translate(-50%, -50%) translate(${visual?.roulette_label_offset_x ?? 0}px, ${visual?.roulette_label_offset_y ?? -8}px) rotate(${visual?.roulette_label_rotation ?? -8}deg)`,
            background: visual?.roulette_label_bg_color ?? "#FFFFFF",
            color: visual?.roulette_label_color ?? "#0F172A",
            border: `${visual?.roulette_label_border_width ?? 0}px solid ${visual?.roulette_label_border_color ?? "#0F172A"}`,
            fontFamily: `"${visual?.roulette_label_font_family ?? "Fredoka"}", sans-serif`,
            fontSize: `${visual?.roulette_label_font_size ?? 14}px`,
            fontWeight: (visual?.roulette_label_font_bold ?? true) ? 900 : 500,
            padding: `${visual?.roulette_label_padding_y ?? 6}px ${visual?.roulette_label_padding_x ?? 18}px`,
            borderRadius: 999,
            boxShadow: "0 6px 14px rgba(0,0,0,.18)",
            letterSpacing: "0.5px",
            whiteSpace: "nowrap",
          }}
        >
          {labelText}
        </div>
      )}
      {/*
        RouletteCard — same optimization as CardFront: don't unmount/remount
        this heavy subtree on every round. Keep the DOM stable, let React
        reuse it, and swap the snippet strings + active-position highlight
        in place. This eliminates the ~200-400 ms of framer-motion exit +
        enter reconciliation on desktop. The 3D tilt (rotateY/rotateX/rotate)
        is applied as a plain CSS transform since we no longer animate it.
      */}
      <div
        key="roulette-card-stable"
        className="absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateY(${tiltY}deg) rotateX(${tiltX}deg) rotate(${tiltZ}deg)`,
          boxShadow: showShadow ? ROULETTE_SHADOW : "none",
          borderRadius: 8,
        }}
      >
          <BoardSurface className="w-full h-full" borderColor={borderColor} borderWidth={borderWidth} surfaceColor={surfaceColor}>
            <div className="h-full p-2 md:p-2.5 grid grid-cols-[1fr_1.05fr] gap-1.5 md:gap-2">
              {/* LEFT: positions mini grid (A-J for original, A-G for quiz) */}
              <div className={leftGridClass}>
                {positions.map((pos) => {
                  // Prefer the pre-computed snippet (pulled from a DIFFERENT
                  // question than the one displayed on the front card — see
                  // GamePlay.computeRouletteSnippets). Falls back to a stable
                  // pick from the displayed question when no snippet was
                  // provided (e.g. older code paths / legacy modes).
                  let snippet = (snippetsByPos && snippetsByPos[pos]) || "";
                  if (!snippet) {
                    const q = displayedByPos?.[pos];
                    if (q?.options && q.options.length > 0) {
                      const idStr = String(q.id || pos);
                      const hash = idStr.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
                      const opts3 = q.options.slice(0, 3).filter(Boolean);
                      if (opts3.length > 0) {
                        snippet = opts3[hash % opts3.length] || "";
                      }
                    }
                  }
                  const miniFontFamily = visual?.roulette_mini_font_family || "Fredoka";
                  const miniFontSize = visual?.roulette_mini_font_size ?? 9;
                  const miniFontColor = visual?.roulette_mini_font_color || "#334155";
                  const miniFontBold = visual?.roulette_mini_font_bold ?? true;
                  return (
                    <div key={pos} className="flex items-center gap-1">
                      <div className="w-4 md:w-5 flex-shrink-0 grid place-items-center">
                        <YellowLetter char={pos} size="xs" sizePx={visual?.small_letter_size} outlineColor={visual?.small_letter_outline_color} outlineWidth={visual?.small_letter_outline_width} outlineMode={visual?.small_letter_outline_mode} outlineColor2={visual?.small_letter_outline_color2} />
                      </div>
                      <div
                        className="flex-1 bg-white rounded-[5px] border-[1.5px] border-white shadow-[inset_0_0_0_1px_#cbd5e1,0_1px_0_rgba(0,0,0,0.25)] min-h-[18px] md:min-h-[22px] px-1 md:px-1.5 grid items-center overflow-hidden"
                        data-testid={`roleta-cell-${pos}`}
                      >
                        <div
                          className="leading-none truncate text-center"
                          style={{
                            fontFamily: miniFontFamily,
                            fontSize: `${miniFontSize}px`,
                            color: miniFontColor,
                            fontWeight: miniFontBold ? 700 : 500,
                          }}
                          title={snippet}
                        >
                          {snippet}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* RIGHT: history strip + big reveal */}
              <div className="flex flex-col gap-1.5 md:gap-2">
                <div className={topStripClass}>
                  {quads.map((q, i) => {
                    const isPlayerLevel = i === playerLevel - 1;
                    const DEFAULT_TOPS = [
                      { from: "#FFEB3B", to: "#FFF59D" },
                      { from: "#FFC107", to: "#FF9800" },
                      { from: "#FF9800", to: "#E64A19" },
                      { from: "#F44336", to: "#FF9800" },
                      { from: "#E64A19", to: "#FFEB3B" },
                    ];
                    const grads = Array.isArray(visual?.cycle_top_gradients) && visual.cycle_top_gradients.length > 0
                      ? visual.cycle_top_gradients
                      : DEFAULT_TOPS;
                    const g = grads[i] || DEFAULT_TOPS[i] || DEFAULT_TOPS[0];
                    const topGradient = `linear-gradient(180deg, ${g.from || "#FBBF24"} 0%, ${g.to || "#F59E0B"} 100%)`;
                    const isActiveCell = isPlayerLevel && !spinning && q && q === activePosition;
                    const blinkOn = visual?.active_indicator_blink !== false;
                    const activeColor = visual?.active_indicator_color || "#FCD34D";
                    return (
                      <div
                        key={`quad-${i}`}
                        data-testid={`quad-${i + 1}`}
                        className={`relative rounded-[4px] overflow-hidden border transition-colors ${
                          isPlayerLevel ? "border-white" : "border-slate-300/80"
                        } ${spinning ? "animate-pulse" : ""}`}
                        style={{
                          aspectRatio: "1 / 1.7",
                          boxShadow: "0 1px 0 rgba(0,0,0,0.35), inset 0 0 0 1px rgba(0,0,0,0.15)",
                          background: "#FFFFFF",
                          containerType: "inline-size",
                        }}
                      >
                        <div
                          className="absolute top-0 left-0 right-0 h-[16%]"
                          style={{
                            background: topGradient,
                            boxShadow: "inset 0 -2px 4px rgba(0,0,0,0.25)",
                          }}
                        />
                        <motion.div
                          className="absolute left-0 right-0 bottom-0 top-[16%] overflow-hidden flex items-center justify-center"
                          animate={isActiveCell && blinkOn ? {
                            backgroundColor: ["#FFFFFF", activeColor, "#FFFFFF"],
                            boxShadow: [
                              "inset 0 0 0 0 rgba(0,0,0,0)",
                              `inset 0 0 0 3px ${activeColor}, 0 0 20px ${activeColor}`,
                              "inset 0 0 0 0 rgba(0,0,0,0)",
                            ],
                            scale: [1, 1.06, 1],
                          } : {}}
                          transition={isActiveCell && blinkOn ? { duration: 0.9, repeat: Infinity, ease: "easeInOut" } : { duration: 0 }}
                          style={{ transformOrigin: "center bottom" }}
                        >
                          <span
                            className="font-[Fredoka] font-extrabold leading-none select-none"
                            style={{
                              color: q ? "#FBBF24" : "#E2E8F0",
                              fontSize: `${visual?.cycle_letter_size ?? 85}cqw`,
                              textShadow: getOutlineTextShadow(
                                visual?.cycle_letter_outline_color ?? "#000000",
                                visual?.cycle_letter_outline_width ?? 0.5,
                                visual?.cycle_letter_outline_mode ?? "solid",
                                visual?.cycle_letter_outline_color2 ?? "#666666",
                              ),
                              lineHeight: 1,
                              fontStyle: "italic",
                              transform: "skewX(-2deg)",
                            }}
                          >
                            {q || "?"}
                          </span>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>

                {/* Big reveal card */}
                <div
                  className="flex-1 rounded-md bg-white border-[3px] border-white relative overflow-hidden grid place-items-center shadow-[inset_0_0_0_2px_#cbd5e1]"
                  style={{
                    boxShadow: "0 2px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #e2e8f0",
                    containerType: "inline-size",
                  }}
                >
                  <div
                    style={{
                      transform: `translate(${visual?.roulette_reveal_offset_x ?? 0}px, ${visual?.roulette_reveal_offset_y ?? 0}px)`,
                      transition: "transform 200ms ease-out",
                      pointerEvents: "none",
                    }}
                  >
                    <AnimatePresence mode="popLayout">
                      {(() => {
                        const effectEnabled = visual?.roulette_reveal_effect_enabled !== false;
                        const effectType = visual?.roulette_reveal_effect || "spring";
                        const effectMap = {
                          spring:     { initial: { scale: 0, rotate: -180 }, animate: { scale: 1, rotate: 0 }, exit: { scale: 0 }, transition: { type: "spring", stiffness: 380, damping: 26 } },
                          fade:       { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.15 } },
                          slide_up:   { initial: { y: 40, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: -20, opacity: 0 }, transition: { duration: 0.15 } },
                          slide_down: { initial: { y: -40, opacity: 0 }, animate: { y: 0, opacity: 1 }, exit: { y: 20, opacity: 0 }, transition: { duration: 0.15 } },
                          zoom_in:    { initial: { scale: 0.4, opacity: 0 }, animate: { scale: 1, opacity: 1 }, exit: { scale: 1.2, opacity: 0 }, transition: { duration: 0.15 } },
                          rotate:     { initial: { rotate: -180, opacity: 0 }, animate: { rotate: 0, opacity: 1 }, exit: { rotate: 180, opacity: 0 }, transition: { duration: 0.2 } },
                          flip:       { initial: { rotateY: 90, opacity: 0 }, animate: { rotateY: 0, opacity: 1 }, exit: { rotateY: -90, opacity: 0 }, transition: { duration: 0.18 } },
                          none:       { initial: false, animate: {}, exit: {}, transition: { duration: 0 } },
                        };
                        const fxr = effectEnabled ? (effectMap[effectType] || effectMap.spring) : effectMap.none;
                        const sizePct = visual?.roulette_reveal_size ?? 115;
                        const imgUrl = BRAND_ASSETS[displayChar];
                        return (
                          <motion.div
                            key={effectEnabled ? displayChar : "static"}
                            initial={fxr.initial}
                            animate={fxr.animate}
                            exit={fxr.exit}
                            transition={fxr.transition}
                            className="grid place-items-center select-none"
                            style={{ width: `${sizePct}cqw`, height: `${sizePct}cqw` }}
                          >
                            {imgUrl && revealImgReady ? (
                              <OutlinedImage
                                src={imgUrl}
                                filter={getOutlineFilter(visual?.reveal_outline_color ?? "#000000", visual?.reveal_outline_width ?? 0.5, visual?.reveal_outline_mode ?? "solid", visual?.reveal_outline_color2 ?? "#666666")}
                                nominal={320}
                                alt={displayChar === "*" ? "estrela" : displayChar}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <span
                                className="font-[Fredoka] font-extrabold leading-none"
                                style={{
                                  fontSize: `${sizePct}cqw`,
                                  color: "#FBBF24",
                                  textShadow: [
                                    "-2px -2px 0 #000", "2px -2px 0 #000", "-2px 2px 0 #000", "2px 2px 0 #000",
                                    "0 -2px 0 #000", "0 2px 0 #000", "-2px 0 0 #000", "2px 0 0 #000",
                                    "-3px 0 0 #000", "3px 0 0 #000", "0 -3px 0 #000", "0 3px 0 #000",
                                    "5px 6px 0 rgba(0,0,0,0.35)",
                                    "5px 6px 2px rgba(0,0,0,0.25)",
                                  ].join(", "),
                                  WebkitTextStroke: "2px #FFFFFF",
                                }}
                              >
                                {displayChar === "*" ? "★" : displayChar}
                              </span>
                            )}
                          </motion.div>
                        );
                      })()}
                    </AnimatePresence>
                  </div>
                  <span className="absolute bottom-1 left-1.5 text-[7px] md:text-[8px] font-[Fredoka] font-bold text-slate-400" data-testid="reveal-card-num">{String(cardNum).padStart(2, "0")}</span>
                </div>
              </div>
            </div>
          </BoardSurface>
          {cardLogoCfg.rouletteEnabled !== false && (
            <div
              className="absolute z-30 pointer-events-none"
              style={{
                right: `${cardLogoCfg.rouletteX ?? cardLogoCfg.offsetX}px`,
                bottom: `${cardLogoCfg.rouletteY ?? cardLogoCfg.offsetY}px`,
              }}
            >
              <SupermatikBrand logo={cardLogo} size={cardLogoCfg.rouletteHeight ?? cardLogoCfg.height} />
            </div>
          )}
        </div>
    </div>
  );
};
export const RouletteCard = React.memo(_RouletteCard);

// -------------- Card Front (question board) --------------
// PERF: memoized (same rationale as RouletteCard above).
const _CardFront = function CardFront({ questions, activePosition, feedback, round, spinning, effect, visual, cardLogo, cardLogoCfg = { height: 44, offsetX: -10, offsetY: 10 }, format = "original", demoHighlight = null }) {
  const fmtNorm = format === "quiz" ? "quiz" : "original";
  const positions = POSITIONS_BY_FORMAT[fmtNorm];
  const gridClass = fmtNorm === "quiz"
    ? "grid grid-cols-1 grid-rows-7 gap-2 md:gap-2.5 h-full pb-2"
    : "grid grid-cols-2 grid-rows-5 grid-flow-col gap-2 md:gap-2.5 h-full pb-2";
  const surfaceColor = fmtNorm === "quiz"
    ? (visual?.card_surface_color && visual.card_surface_color !== "#173b78"
        ? visual.card_surface_color
        : QUIZ_CARD_SURFACE)
    : (visual?.card_surface_color || "#173b78");
  const fx = getEffect(effect);
  const textStyle = getQuestionTextStyle(visual);
  const borderColor = visual?.card_front_border_color || "#03060f";
  const borderWidth = visual?.card_front_border_width ?? 22;
  // Per-game configurable 3D + 2D tilts (mirrors RouletteCard logic).
  const tiltY = Number.isFinite(visual?.card_front_tilt_y) ? Number(visual.card_front_tilt_y) : CARDFRONT_TILT_Y;
  const tiltX = Number.isFinite(visual?.card_front_tilt_x) ? Number(visual.card_front_tilt_x) : 0;
  const tiltZ = Number.isFinite(visual?.card_front_tilt_z) ? Number(visual.card_front_tilt_z) : 0;
  const persp = Math.max(400, Math.min(4000, Number(visual?.perspective_strength) || 1400));
  const showShadow = visual?.card_depth_shadow !== false;
  const wrapScale = Math.max(0.5, Math.min(2, Number(visual?.card_front_scale) || 1));
  const wrapOffsetX = Number.isFinite(visual?.card_front_offset_x) ? Number(visual.card_front_offset_x) : 0;
  const wrapOffsetY = Number.isFinite(visual?.card_front_offset_y) ? Number(visual.card_front_offset_y) : 0;
  const tiltedFx = {
    initial: { ...fx.initial, rotateY: fx.initial?.rotateY ?? tiltY, rotateX: tiltX, rotate: tiltZ },
    animate: { ...fx.animate, rotateY: tiltY, rotateX: tiltX, rotate: tiltZ },
    exit: { ...fx.exit, rotateY: fx.exit?.rotateY ?? tiltY, rotateX: tiltX, rotate: tiltZ },
    transition: fx.transition,
  };
  return (
    <div
      className="relative mx-auto w-full max-w-[600px] supert-card-tilt-right cardfront-wrap"
      style={{
        aspectRatio: "1.414 / 1",
        maxHeight: "calc(100dvh - 280px)",
        perspective: `${persp}px`,
        perspectiveOrigin: "30% 50%",
        transform: `translate(${wrapOffsetX}px, ${wrapOffsetY}px) scale(${wrapScale})`,
        transformOrigin: "center center",
      }}
    >
      {/*
        CardFront intentionally NOT wrapped in AnimatePresence — the mini-grid
        tree is heavy (letters, borders, filters, brand-logo) and re-mounting
        it every round used to take 800-1300 ms of main-thread reconciliation
        (framer-motion exit + enter). Since only the QUESTION TEXT changes
        between rounds, we let React reuse the DOM subtree and swap the string
        in place — near-instant. The border color still transitions via the
        `transition-all` utility for the emerald/rose feedback flash.
      */}
      <div
        className="absolute inset-0"
        style={{
          transformStyle: "preserve-3d",
          transform: `rotateY(${tiltY}deg) rotateX(${tiltX}deg) rotate(${tiltZ}deg)`,
          borderRadius: 8,
          boxShadow: showShadow ? CARDFRONT_SHADOW : "none",
        }}
      >
          <BoardSurface className="p-3 md:p-4 w-full h-full" borderColor={borderColor} borderWidth={borderWidth} surfaceColor={surfaceColor}>
            <div className={gridClass}>
              {positions.map((pos) => {
                const q = questions?.[pos];
                const isActive = pos === activePosition && !spinning;
                // DEMO phrase_2 — flag the row that matches the roulette's
                // drawn letter so the player visually connects the letter
                // (left side) with its question (central card). Only
                // highlights when the engine isn't already showing an
                // answer-feedback border and we're not mid-spin.
                const isDemoHighlighted = demoHighlight && demoHighlight === pos && !spinning && !feedback;
                const stateBorder = isActive
                  ? feedback?.correct
                    ? "border-emerald-400"
                    : feedback && !feedback.correct
                      ? "border-rose-400"
                      : "border-slate-400"
                  : "border-slate-300";
                return (
                  <div key={pos} data-testid={`front-cell-${pos}`} className="flex items-center gap-2 min-h-0">
                    <div className="w-7 md:w-9 flex-shrink-0 grid place-items-center">
                      <YellowLetter char={pos} size="md" sizePx={visual?.front_letter_size} outlineColor={visual?.small_letter_outline_color} outlineWidth={visual?.small_letter_outline_width} outlineMode={visual?.small_letter_outline_mode} outlineColor2={visual?.small_letter_outline_color2} />
                    </div>
                    <div
                      data-demo-highlight={isDemoHighlighted ? "1" : undefined}
                      className={`flex-1 rounded-md border-[2px] ${isDemoHighlighted ? "border-rose-500 bg-rose-50 ring-2 ring-rose-300 shadow-[0_0_0_4px_rgba(244,63,94,0.25)]" : `bg-white ${stateBorder}`} px-2 py-3 md:py-3.5 transition-all shadow-[0_2px_0_rgba(0,0,0,0.25)] min-w-0`}
                    >
                      <div
                        className="leading-none truncate"
                        style={isDemoHighlighted ? { ...textStyle, color: "#be123c", fontWeight: 800 } : textStyle}
                      >
                        {q?.question || "—"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </BoardSurface>
          {cardLogoCfg.frontEnabled !== false && (
            <div
              className="absolute z-30 pointer-events-none"
              style={{
                right: `${cardLogoCfg.frontX ?? cardLogoCfg.offsetX}px`,
                bottom: `${cardLogoCfg.frontY ?? cardLogoCfg.offsetY}px`,
              }}
            >
              <SupermatikBrand
                logo={cardLogo}
                size={cardLogoCfg.frontHeight ?? cardLogoCfg.height}
                filter={`hue-rotate(${cardLogoCfg.hue ?? 0}deg) saturate(${cardLogoCfg.saturate ?? 1}) brightness(${cardLogoCfg.brightness ?? 1})${cardLogoCfg.invert ? " invert(1)" : ""}`}
              />
            </div>
          )}
      </div>
    </div>
  );
};
export const CardFront = React.memo(_CardFront);

// -------------- Options grid (2-4 answers) --------------
//
// Renders only the non-empty options so True/False questions (2 options) or
// 3-way questions don't show empty "—" placeholders. The original 1-based
// index is preserved on each rendered button (so a question with options
// [a, "", c, d] still maps correctly to the backend's `correct` index).
// PERF: memoized so timer-driven parent re-renders don't trigger option
// re-render at 60 Hz.
//
// `feedback` shape:
//   { correctIndex, chosen, correct, simultaneous?, robotFirst? }
//
// `simultaneous` (VS Robot) — user clicked WITHIN ~500ms of the robot.
//   Correct → LIGHTER emerald so the user visually distinguishes it from
//             a solo win.  (Nota 2 do produto)
//   Wrong   → DARKER rose so the user distinguishes it from a solo mistake.
//             (Nota 3 do produto)
// `robotFirst` — robot resolved BEFORE the user clicked. The whole grid is
//   locked (options darkened + pointer-events off) so the user can't click a
//   button after the round has been decided by the robot. (Nota 1 do produto)
const _Options = function Options({ question, feedback, onAnswer, disabled }) {
  const allOpts = question?.options || ["", "", "", ""];
  const visible = allOpts
    .map((opt, i) => ({ opt: (opt || "").toString().trim(), idx: i + 1 }))
    .filter((o) => o.opt.length > 0);
  const items = visible.length > 0 ? visible : allOpts.map((opt, i) => ({ opt: opt || "—", idx: i + 1 }));
  const gridClass = items.length === 1
    ? "grid grid-cols-1 gap-2 mt-1"
    : items.length === 3
      ? "grid grid-cols-3 gap-2 mt-1"
      : "grid grid-cols-2 gap-2 mt-1";
  const robotFirst = !!feedback?.robotFirst;
  const simultaneous = !!feedback?.simultaneous;
  const gridDim = robotFirst; // full darken when robot answered first
  return (
    <div className={gridClass} data-robot-first={robotFirst || undefined}>
      {items.map(({ opt, idx }) => {
        const isCorrect = feedback?.correctIndex === idx && feedback?.correct;
        const isWrong = feedback?.chosen === idx && feedback && !feedback.correct;
        // Colour class — lighter/darker variants for simultaneous outcomes
        // so the user can tell a shared race from a solo one at a glance.
        const colour = isCorrect
          ? simultaneous
            ? "bg-emerald-300 text-emerald-900 border-emerald-500"   // Nota 2 — LIGHTER green
            : "bg-emerald-500 text-white border-emerald-700"
          : isWrong
            ? simultaneous
              ? "bg-rose-700 text-white border-rose-900"             // Nota 3 — DARKER red
              : "bg-rose-500 text-white border-rose-700"
            : "bg-slate-300 hover:bg-slate-200 text-slate-800 border-slate-500";
        const isDisabled = disabled || !!feedback;
        return (
          <button
            key={idx}
            data-testid={`opt-${idx}`}
            disabled={isDisabled}
            onClick={() => onAnswer(idx)}
            style={{ touchAction: "manipulation" }}
            className={`py-2.5 md:py-3 px-4 rounded-full font-[Fredoka] font-bold text-base md:text-lg shadow-md transition-all border-b-4 select-none enabled:active:translate-y-1 enabled:active:border-b-0 disabled:pointer-events-none ${colour} ${gridDim && !isCorrect && !isWrong ? "opacity-40 grayscale" : disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};
export const Options = React.memo(_Options);

// -------------- HUD pills --------------
export function BrandBox() {
  return (
    <div className="bg-white/30 rounded-xl px-3 py-2 backdrop-blur-sm border border-white/40">
      <div className="font-[Fredoka] font-bold text-lg md:text-xl text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.3)] leading-none">
        super<span className="text-amber-300">T</span>
      </div>
      <div className="font-[Fredoka] font-bold text-lg md:text-xl text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.3)] -mt-0.5">matik</div>
    </div>
  );
}

export function AttemptsPill({ current, max }) {
  const cur = Math.max(1, Math.min(max, current));
  return (
    <div
      className="bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-full px-3 py-1.5 shadow text-center font-[Fredoka] font-bold text-xs md:text-sm border-2 border-white tracking-wider"
      data-testid="attempts-pill"
    >
      TENTATIVAS: <span className="font-mono">{cur}/{max}</span>
    </div>
  );
}

// PERF: self-updating — runs its OWN rAF loop reading `getElapsed()` so the
// parent (GamePlay) never re-renders per timer tick. setState bails out when
// the formatted string is unchanged, so re-renders stay confined to this pill.
//
// `pillsCfg` (opcional) segue o schema de homepage_config.gameplay_pills:
//   { timer: {...}, level: {...} }
// `lang` (opcional, default "pt") é usado para escolher o prefixo multilíngue.
export function TimerPill({ getElapsed, time = 0, level, levelLabel, penaltyPulse = false, penaltyAmount = 0, pillsCfg, lang = "pt" }) {
  const [liveStr, setLiveStr] = useState(() => formatTime(getElapsed ? getElapsed() : time));
  useEffect(() => {
    if (!getElapsed) return undefined;
    let raf;
    const loop = () => {
      setLiveStr(formatTime(getElapsed()));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [getElapsed]);
  const tstr = getElapsed ? liveStr : formatTime(time);
  const tCfg = pillsCfg?.timer || {};
  const lCfg = pillsCfg?.level || {};
  const tLabel = (tCfg.label?.[lang] || "").trim();
  const lLabel = (lCfg.label?.[lang] || "").trim();
  const timerStyle = {
    backgroundColor: tCfg.bg_color || undefined,
    borderColor: tCfg.border_color || undefined,
    color: tCfg.font_color || undefined,
    fontSize: tCfg.font_size ? `${tCfg.font_size}px` : undefined,
    fontWeight: tCfg.weight || undefined,
    justifyContent: tCfg.text_align === "center" ? "center" : tCfg.text_align === "right" ? "flex-end" : undefined,
    textTransform: tCfg.uppercase ? "uppercase" : undefined,
    transform: tCfg.offset_y ? `translateY(${tCfg.offset_y}px)` : undefined,
    boxShadow: tCfg.shadow === false ? "none" : undefined,
  };
  const timerSubStyle = { color: tCfg.sub_color || undefined };
  const levelBoxText = lLabel ? `${lLabel} ${levelLabel || `Nível ${level}`}` : (levelLabel || `Nível ${level}`);
  const levelStyle = {
    backgroundColor: lCfg.bg_color || undefined,
    borderColor: lCfg.border_color || undefined,
    color: lCfg.font_color || undefined,
    fontSize: lCfg.font_size ? `${lCfg.font_size}px` : undefined,
    fontWeight: lCfg.weight || undefined,
    textAlign: lCfg.text_align || undefined,
    textTransform: lCfg.uppercase ? "uppercase" : undefined,
    transform: lCfg.offset_y ? `translateY(${lCfg.offset_y}px)` : undefined,
    boxShadow: lCfg.shadow === false ? "none" : undefined,
  };
  return (
    <div className="space-y-1.5">
      <div
        className={`relative rounded-full px-3 py-2 shadow flex items-center gap-2 border-2 transition-colors duration-200 ${
          penaltyPulse
            ? "bg-amber-100 border-amber-400 animate-pulse"
            : "bg-white border-white"
        }`}
        style={penaltyPulse ? undefined : timerStyle}
        data-testid="game-timer"
      >
        <TimerIcon className={`w-4 h-4 md:w-5 md:h-5 ${penaltyPulse ? "text-amber-700" : "text-slate-700"}`} style={penaltyPulse ? undefined : { color: tCfg.font_color || undefined }} />
        {tLabel && !penaltyPulse && (
          <span className="font-[Fredoka] text-xs md:text-sm font-bold mr-0.5" style={{ color: tCfg.font_color || undefined }}>
            {tLabel}
          </span>
        )}
        <div className="flex items-baseline gap-1">
          <span
            className={`font-mono ${penaltyPulse ? "text-lg md:text-xl font-bold text-amber-900" : "font-bold"}`}
            style={penaltyPulse ? undefined : { fontSize: tCfg.font_size ? `${tCfg.font_size}px` : undefined, color: tCfg.font_color || undefined, fontWeight: tCfg.weight || undefined }}
          >
            {tstr.slice(0, 5)}
          </span>
          <span
            className={`font-mono ${penaltyPulse ? "text-xs md:text-sm font-bold text-amber-700" : "font-bold"}`}
            style={penaltyPulse ? undefined : { fontSize: tCfg.font_size ? `${Math.max(10, tCfg.font_size * 0.7)}px` : undefined, ...timerSubStyle, fontWeight: tCfg.weight || undefined }}
          >
            :{tstr.slice(6)}
          </span>
        </div>
        {penaltyPulse && penaltyAmount > 0 && (
          <span
            className="absolute -top-3 -right-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-extrabold shadow-md pointer-events-none"
            style={{ animation: "supert-penalty-float 0.6s ease-out forwards" }}
            data-testid="timer-penalty-badge"
          >
            +{penaltyAmount}s
          </span>
        )}
      </div>
      <div
        className="bg-white rounded-full px-3 py-1.5 shadow text-center font-[Fredoka] font-bold text-sm md:text-base text-slate-900 border-2 border-white"
        style={levelStyle}
        data-testid="game-level"
      >
        {levelBoxText}
      </div>
    </div>
  );
}

// -------------- VS ROBOT — Robot profile box (top-right of GamePlay) --------------
// Mirrors ProfileCard's visual structure 1-to-1 (same gradient header, same
// overlapping avatar with halo, same name+subtitle layout) so the robot side
// looks like a peer to the user side.
export function RobotProfileBox({ robot, levelLabel, fx, awards, pillsCfg, lang = "pt" }) {
  if (!robot) return null;
  const SIZE = 150;
  const STROKE = 6;
  const fxAnim =
    fx === "bounce" ? { y: [0, -10, 0, -6, 0], scale: [1, 1.1, 1, 1.05, 1], rotate: [0, -5, 5, -3, 0] } :
    fx === "shake"  ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } :
    {};
  return (
    <div className="space-y-1.5">
      <div
        className="relative bg-white rounded-2xl shadow-md overflow-visible"
        data-testid="vsr-robot-profile"
      >
        {/* FloatingAwards next to the robot profile removed (Feb 2026 — user
            feedback). Progress is now shown ONLY on the SupertBar to avoid
            visual duplication. `awards` prop still flows through the tree
            for future analytics but is intentionally not rendered here. */}
        {/* Top section — sky/cyan gradient with "ROBOT" header + stats badges */}
        <div className="relative rounded-t-2xl bg-gradient-to-b from-sky-100 to-cyan-50 px-3 pt-2 pb-3 pr-[140px]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 leading-tight truncate">
            ROBOT
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold">
              ⚡ {robot.efficacy}%
            </span>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-bold">
              ⏱ {robot.speed_min}-{robot.speed_max}s
            </span>
            {typeof robot.mental_strength === "number" && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold" data-testid="vsr-robot-mental-strength" title="Força mental — define os pontos no ranking">
                🧠 {robot.mental_strength}/10
              </span>
            )}
          </div>
        </div>

        {/* Bottom section — white, with name + alcunha */}
        <div className="px-3 py-3 pr-[140px] rounded-b-2xl">
          <div
            className="font-[Fredoka] font-extrabold text-slate-900 leading-tight truncate text-lg md:text-xl"
            data-testid="vsr-robot-name"
            title={robot.name}
          >
            {robot.name}
          </div>
          {robot.alcunha ? (
            <div className="text-[11px] text-slate-500 truncate mt-1 italic" title={robot.alcunha}>
              {robot.alcunha}
            </div>
          ) : null}
        </div>

        {/* Avatar — overlapping the card on the right with yellow halo */}
        <motion.div
          className="absolute -top-5 -right-4 z-10"
          style={{ width: SIZE, height: SIZE }}
          aria-label={robot.name}
          animate={fxAnim}
          transition={{ duration: fx ? 0.6 : 0 }}
        >
          {/* Yellow soft halo behind avatar */}
          <div
            className="absolute"
            style={{
              top: STROKE + 2,
              left: STROKE + 2,
              width: SIZE - 2 * (STROKE + 2),
              height: SIZE - 2 * (STROKE + 2),
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 50% 35%, rgba(253,224,71,0.95) 0%, rgba(250,204,21,0.55) 65%, rgba(250,204,21,0) 100%)",
             
            }}
          />
          {/* Sky-blue inner circle with the robot avatar image */}
          <div
            className="absolute overflow-hidden grid place-items-center bg-sky-200 ring-2 ring-white shadow"
            style={{
              top: STROKE + 6,
              left: STROKE + 6,
              width: SIZE - 2 * (STROKE + 6),
              height: SIZE - 2 * (STROKE + 6),
              borderRadius: "50%",
            }}
          >
            <img
              src={robot.image_url}
              alt={robot.name}
              className="w-full h-full object-contain p-1"
              draggable={false}
            />
          </div>
        </motion.div>
      </div>
      {levelLabel && (
        <div
          className="bg-white rounded-full px-3 py-1.5 shadow text-center font-[Fredoka] font-bold text-sm md:text-base text-slate-900 border-2 border-white"
          style={(() => {
            const lCfg = pillsCfg?.level || {};
            return {
              backgroundColor: lCfg.bg_color || undefined,
              borderColor: lCfg.border_color || undefined,
              color: lCfg.font_color || undefined,
              fontSize: lCfg.font_size ? `${lCfg.font_size}px` : undefined,
              fontWeight: lCfg.weight || undefined,
              textAlign: lCfg.text_align || undefined,
              textTransform: lCfg.uppercase ? "uppercase" : undefined,
              transform: lCfg.offset_y ? `translateY(${lCfg.offset_y}px)` : undefined,
              boxShadow: lCfg.shadow === false ? "none" : undefined,
            };
          })()}
          data-testid="vsr-game-level"
        >
          {(pillsCfg?.level?.label?.[lang] || "").trim()
            ? `${pillsCfg.level.label[lang].trim()} ${levelLabel}`
            : levelLabel}
        </div>
      )}
    </div>
  );
}

// -------------- VS HUMANO — Human opponent profile box (top-right of GamePlay) --------------
// Mirrors RobotProfileBox 1-to-1 but renders the opponent's avatar via AvatarSVG
// and uses a violet/fuchsia palette to distinguish "human peer" from "robot".
export function HumanOpponentBox({ opponent, levelLabel, fx, awards, pillsCfg, lang = "pt" }) {
  // Locale-aware fallbacks — HumanOpponentBox doesn't call useI18n directly
  // to avoid re-renders on every language event; the parent passes `lang`.
  const _fallbackName = lang === "en" ? "opponent" : lang === "es" ? "rival" : "adversário";
  const _realtimeSub = lang === "en" ? "Real-time duel" : lang === "es" ? "Duelo en tiempo real" : "Duelo em tempo real";
  const _opponentHeader = lang === "en" ? "OPPONENT" : lang === "es" ? "RIVAL" : "ADVERSÁRIO";
  const _humanBadge = lang === "en" ? "Human" : lang === "es" ? "Humano" : "Humano";
  if (!opponent) return null;
  const SIZE = 150;
  const STROKE = 6;
  const fxAnim =
    fx === "bounce" ? { y: [0, -10, 0, -6, 0], scale: [1, 1.1, 1, 1.05, 1], rotate: [0, -5, 5, -3, 0] } :
    fx === "shake"  ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } :
    {};
  const cfg = opponent.avatar_cfg || null;
  return (
    <div className="space-y-1.5">
      <div
        className="relative bg-white rounded-2xl shadow-md overflow-visible"
        data-testid="vsh-opponent-box"
      >
        {/* FloatingAwards next to the opponent profile removed (Feb 2026 —
            user feedback). Progress is now shown ONLY on the SupertBar. */}
        {/* Top section — violet gradient with "ADVERSÁRIO" header */}
        <div className="relative rounded-t-2xl bg-gradient-to-b from-violet-100 to-fuchsia-50 px-3 pt-2 pb-3 pr-[140px]">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600 leading-tight truncate">
            {_opponentHeader}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold">
              ◉ {_humanBadge}
            </span>
          </div>
        </div>
        {/* Bottom — name */}
        <div className="px-3 py-3 pr-[140px] rounded-b-2xl">
          <div
            className="font-[Fredoka] font-extrabold text-slate-900 leading-tight truncate text-lg md:text-xl"
            data-testid="vsh-opp-name"
            title={opponent.name}
          >
            {opponent.name || _fallbackName}
          </div>
          <div className="text-[11px] text-slate-500 truncate mt-1 italic">{_realtimeSub}</div>
        </div>
        {/* Avatar overlapping right with violet halo */}
        <motion.div
          className="absolute -top-5 -right-4 z-10"
          style={{ width: SIZE, height: SIZE }}
          aria-label={opponent.name}
          animate={fxAnim}
          transition={{ duration: fx ? 0.6 : 0 }}
        >
          <div
            className="absolute"
            style={{
              top: STROKE + 2,
              left: STROKE + 2,
              width: SIZE - 2 * (STROKE + 2),
              height: SIZE - 2 * (STROKE + 2),
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 50% 35%, rgba(216,180,254,0.95) 0%, rgba(192,132,252,0.55) 65%, rgba(192,132,252,0) 100%)",
             
            }}
          />
          <div
            className="absolute overflow-hidden grid place-items-center bg-violet-100 ring-2 ring-white shadow"
            style={{
              top: STROKE + 6,
              left: STROKE + 6,
              width: SIZE - 2 * (STROKE + 6),
              height: SIZE - 2 * (STROKE + 6),
              borderRadius: "50%",
            }}
          >
            {cfg ? (
              <AvatarSVG cfg={cfg} size={SIZE - 2 * (STROKE + 8)} />
            ) : (
              <div className="font-[Fredoka] font-extrabold text-3xl text-violet-700">
                {(opponent.name || "?").trim().charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </motion.div>
      </div>
      {levelLabel && (
        <div
          className="bg-white rounded-full px-3 py-1.5 shadow text-center font-[Fredoka] font-bold text-sm md:text-base text-slate-900 border-2 border-white"
          style={(() => {
            const lCfg = pillsCfg?.level || {};
            return {
              backgroundColor: lCfg.bg_color || undefined,
              borderColor: lCfg.border_color || undefined,
              color: lCfg.font_color || undefined,
              fontSize: lCfg.font_size ? `${lCfg.font_size}px` : undefined,
              fontWeight: lCfg.weight || undefined,
              textAlign: lCfg.text_align || undefined,
              textTransform: lCfg.uppercase ? "uppercase" : undefined,
              transform: lCfg.offset_y ? `translateY(${lCfg.offset_y}px)` : undefined,
              boxShadow: lCfg.shadow === false ? "none" : undefined,
            };
          })()}
          data-testid="vsh-game-level"
        >
          {(pillsCfg?.level?.label?.[lang] || "").trim()
            ? `${pillsCfg.level.label[lang].trim()} ${levelLabel}`
            : levelLabel}
        </div>
      )}
    </div>
  );
}

// -------------- VS ROBOT — Race bar (user vs robot, side-by-side) --------------
/**
 * Animated stack of awarded symbols (letters or stars) that floats near the
 * player profile after each round. New symbols use an amber chip, repeated
 * letters are shown with a strike-through (since they have no value).
 */
export function FloatingAwards({ awards, position = "top-right", testId }) {
  // position controls horizontal anchor of the stack
  const posClass = {
    "top-right": "top-2 right-2",
    "top-left": "top-2 left-2",
    "bottom-right": "bottom-2 right-2",
    "bottom-left": "bottom-2 left-2",
  }[position] || "top-2 right-2";
  // Repeated letters are now shown IN-PLACE inside the SupertBar (as an
  // adjacent mini-card that appears for 1s next to the existing letter),
  // so filter them out of the floating corner pills to avoid duplicate UX.
  const list = (awards || []).filter((a) => !a.was_repeat);
  return (
    <div
      className={`absolute ${posClass} flex gap-1.5 pointer-events-none z-30`}
      data-testid={testId || "vsr-floating-awards"}
    >
      <AnimatePresence initial={false}>
        {list.map((a, i) => {
          const isStar = a.item === "*";
          // Stable key: position in the round (i) + item — survives state filter so
          // kept items don't re-mount when repeated letters are removed.
          return (
            <motion.div
              key={`${i}-${a.item}`}
              // Enter IN PLACE (just fade + scale + slight tilt) instead of
              // sliding up from y:30 below — the old animation made the mini
              // card visibly appear BELOW its final slot first, giving the
              // impression of a stray ghost card that then vanished as it
              // reached its resting position. (Nota 4 do brief do produto.)
              initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.6, rotate: 12 }}
              transition={{ duration: 0.35, delay: i * 0.08, type: "spring", stiffness: 260 }}
              className="w-14 drop-shadow-lg"
              style={{ aspectRatio: "1.4 / 1" }}
            >
              <MiniRouletteCard
                char={isStar ? "*" : a.item}
                isFilled={true}
                isStar={isStar}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

function FloatingLetter({ letter, isRepeat }) {
  if (!letter) return null;
  const isStar = letter === "*";
  return (
    <motion.div
      key={`${letter}-${Math.random()}`}
      initial={{ opacity: 0, y: 10, scale: 0.6 }}
      animate={{ opacity: 1, y: -28, scale: 1 }}
      exit={{ opacity: 0, y: -42, scale: 0.8 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      className="absolute left-1/2 -translate-x-1/2 -top-2 pointer-events-none z-20"
      data-testid={`race-floating-${isRepeat ? "repeat" : "new"}`}
    >
      <div
        className={`px-2 py-0.5 rounded-md text-sm font-extrabold font-[Fredoka] shadow ${
          isRepeat
            ? "bg-slate-200 text-slate-500 line-through decoration-2"
            : "bg-amber-400 text-white"
        }`}
        title={isRepeat ? "Letra repetida — sem valor" : "+1"}
      >
        {isStar ? "★" : letter}{isRepeat ? "" : "+"}
      </div>
    </motion.div>
  );
}

function RaceSide({ side, name, avatar, avatarNode, progress, supertCount, fx }) {
  const lettersGot = new Set(progress.filter((p) => p !== "*"));
  const starsGot = progress.filter((p) => p === "*").length;
  const align = side === "user" ? "flex-row" : "flex-row-reverse";
  const textAlign = side === "user" ? "text-left" : "text-right";
  const dotsAlign = side === "user" ? "" : "flex-row-reverse";
  // Animation keyframes for the avatar based on fx ("bounce"|"shake"|null)
  const fxAnim =
    fx === "bounce" ? { y: [0, -10, 0, -6, 0], scale: [1, 1.1, 1, 1.05, 1] } :
    fx === "shake"  ? { x: [0, -6, 6, -4, 4, 0] } :
    {};
  return (
    <div className={`flex items-center gap-2 ${align} flex-1 min-w-0`} data-testid={`race-side-${side}`}>
      <motion.div
        animate={fxAnim}
        transition={{ duration: fx ? 0.6 : 0 }}
        className="relative shrink-0"
      >
        {avatarNode ? avatarNode :
          avatar ? (
            <img src={avatar} alt={name} className="w-9 h-9 rounded-full object-cover bg-slate-100 border-2 border-white shadow shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-200 grid place-items-center text-slate-500 text-xs font-bold shrink-0">{(name || "?")[0]}</div>
          )
        }
      </motion.div>
      <div className={`flex flex-col min-w-0 ${textAlign}`}>
        <div className="text-[11px] font-bold text-slate-900 truncate leading-tight">{name}</div>
        <div className={`flex items-center gap-0.5 mt-0.5 ${dotsAlign}`} data-testid={`race-dots-${side}`}>
          {SUPERT.map((ch) => (
            <span
              key={ch}
              className={`inline-block w-3 h-3 rounded-sm overflow-hidden ${lettersGot.has(ch) ? "" : "bg-white/60"}`}
              title={ch}
            >
              {lettersGot.has(ch) ? (
                <img
                  src={BRAND_ASSETS[ch]}
                  alt={ch}
                  draggable={false}
                  className="w-full h-full object-contain pointer-events-none"
                />
              ) : null}
            </span>
          ))}
          {Array.from({ length: supertCount }).map((_, i) => (
            <span
              key={`s${i}`}
              className={`inline-block w-3 h-3 rounded-full ml-0.5 overflow-hidden ${i < starsGot ? "" : "bg-white/40"}`}
              title="estrela"
            >
              {i < starsGot ? (
                <img
                  src={BRAND_ASSETS["*"]}
                  alt="estrela"
                  draggable={false}
                  className="w-full h-full object-contain pointer-events-none"
                />
              ) : null}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact race bar shown at the top of the center column during a VS ROBOT
 * duel. Layout: [user avatar | name | dots-→] · VS · [←-dots | name | robot avatar].
 * Each side has 6 small dots (S-U-P-E-R-T) that light up as letters are
 * collected, plus a row of stars equal to `supertCount` showing earned stars.
 */
export function RaceBar({ userName, userAvatarUrl, userAvatarNode, userProgress = [], robotName, robotImage, robotAvatarNode, robotProgress = [], supertCount = 1, robotFx = null, oppTestIdSide = "robot" }) {
  return (
    <div className="w-full rounded-2xl bg-white/85 shadow-sm border-2 border-white px-3 py-1.5 flex items-center gap-2" data-testid="race-bar">
      <RaceSide side="user" name={userName || "Eu"} avatar={userAvatarUrl} avatarNode={userAvatarNode} progress={userProgress} supertCount={supertCount} />
      <div className="px-2 py-0.5 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-[10px] font-extrabold font-[Fredoka] shrink-0" data-testid="race-vs">VS</div>
      <RaceSide side={oppTestIdSide} name={robotName || "Robot"} avatar={robotImage} avatarNode={robotAvatarNode} progress={robotProgress} supertCount={supertCount} fx={robotFx} />
    </div>
  );
}



// -------------- SUPERT progress bar --------------

/**
 * Compact HTML/CSS replica of the real SUPERTMATIK roulette card, used as the
 * 6 slots inside SupertBar. The design mirrors the physical card:
 *   • pink triangle top-left + green triangle bottom-left (decorative)
 *   • LEFT column (~38%): A-J grid (2 sub-cols × 5 rows) of letter + number cells
 *   • RIGHT column (~62%): ADHIA top tabs + white panel with big yellow letter +
 *                          "supertmatik" tiny brand at the bottom
 * When `isFilled=false` the white panel stays empty (no ghost letter), per spec.
 */
export function MiniRouletteCard({ char, isFilled, isStar, cardNumbers = [5, 1, 9, 14, 8, 13, 13, 6, 61, 76], shimmer = false }) {
  // Stylized brand image for letters AND the star.
  const brandUrl = isFilled ? BRAND_ASSETS[isStar ? "*" : char] : null;
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 45%, #2563eb 100%)",
        // IMPORTANT — the outer element's OWN border / borderRadius / boxShadow
        // MUST stay in absolute pixels. cqw resolves against the nearest
        // ancestor container-query container, and since this element IS the
        // container (not inside one), cqw would fall back to the viewport
        // width, giving huge pill-shaped rounded corners and thick borders.
        // Only the INNER cells (below) may use cqw — those resolve against
        // THIS card via `containerType: inline-size`.
        border: "2px solid #0a1428",
        borderRadius: 4,
        boxShadow: isFilled
          ? "0 0 8px rgba(251,191,36,.55), 0 1px 0 rgba(0,0,0,.35)"
          : "0 1px 0 rgba(0,0,0,.35)",
        containerType: "inline-size",
      }}
    >
      {/* Diagonal stripes background, mimicking the real card */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "repeating-linear-gradient(135deg, rgba(255,255,255,.10) 0 2px, transparent 2px 8px)",
          opacity: 0.55,
        }}
      />
      {/* Shimmer overlay — diagonal light sweep, only on won cards */}
      {shimmer && isFilled && (
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 5 }}
        >
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: "-50%",
              width: "50%",
              background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.85) 50%, transparent 70%)",
              mixBlendMode: "screen",
              animation: "mini-card-shimmer 2.8s linear infinite",
            }}
          />
        </div>
      )}
      {/* Pink triangle top-left corner */}
      <div
        aria-hidden
        className="absolute"
        style={{
          top: 0, left: 0, width: "32%", height: "32%",
          background: "linear-gradient(135deg, #f472b6 0%, #db2777 70%, #9d174d 100%)",
          clipPath: "polygon(0 0, 100% 0, 0 100%)",
        }}
      />
      {/* Green triangle bottom-left corner */}
      <div
        aria-hidden
        className="absolute"
        style={{
          bottom: 0, left: 0, width: "28%", height: "28%",
          background: "linear-gradient(45deg, #16a34a 0%, #15803d 70%, #064e3b 100%)",
          clipPath: "polygon(0 100%, 100% 100%, 0 0)",
        }}
      />

      {/* Two columns: left = A-J grid (~38%), right = ADHIA + big letter (~62%) */}
      <div className="absolute inset-0 grid" style={{ gridTemplateColumns: "38% 62%", padding: "4%", gap: "3%" }}>
        {/* LEFT column: A-J grid (10 cells, 2 sub-cols × 5 rows, column-flow) */}
        <div className="relative grid grid-cols-2 grid-rows-5 grid-flow-col" style={{ gap: "0.9cqw" }}>
          {POSITIONS.map((pos, idx) => {
            const num = cardNumbers[idx] ?? 0;
            return (
              <div key={pos} className="flex items-stretch" style={{ gap: "0.6cqw" }}>
                <div
                  className="grid place-items-center font-[Fredoka] font-extrabold"
                  style={{
                    background: "#fbbf24",
                    color: "#7c2d12",
                    width: "42%",
                    // Pure cqw — no px cap. On zoom, cqw scales with the card
                    // (which scales with parent %), keeping the letter perfectly
                    // proportional at 50%, 100%, 200%, etc.
                    fontSize: "5.4cqw",
                    lineHeight: 1,
                    borderRadius: "0.6cqw",
                    border: "0.3cqw solid rgba(0,0,0,.35)",
                  }}
                >
                  {pos}
                </div>
                <div
                  className="flex-1 grid place-items-center bg-white font-[Fredoka] font-bold text-slate-800"
                  style={{
                    fontSize: "5.4cqw",
                    lineHeight: 1,
                    borderRadius: "0.6cqw",
                    border: "0.3cqw solid rgba(0,0,0,.2)",
                  }}
                >
                  {num}
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT column: ADHIA tabs row + big letter panel + supertmatik */}
        <div className="flex flex-col" style={{ gap: "2%" }}>
          {/* ADHIA tabs (5 mini boxes) — level indicator strip */}
          <div className="grid grid-cols-5" style={{ gap: "0.6cqw", height: "14%" }}>
            {["A", "D", "H", "I", "A"].map((L, i) => (
              <div
                key={`fake-cell-${i}-${L}`}
                className="grid place-items-center font-[Fredoka] font-extrabold"
                style={{
                  background: i === 0 ? "#fbbf24" : "#fef3c7",
                  color: "#7c2d12",
                  fontSize: "4.6cqw",
                  lineHeight: 1,
                  borderRadius: "0.6cqw",
                  border: "0.3cqw solid rgba(0,0,0,.35)",
                }}
              >
                {L}
              </div>
            ))}
          </div>
          {/* Big letter panel (white, with letter shown only when won) */}
          <div
            className="relative flex-1 grid place-items-center overflow-hidden"
            style={{
              background: isFilled
                ? "radial-gradient(ellipse at 50% 38%, #FFFDF1 0%, #FFF6D6 55%, #FDE9A8 100%)"
                : "#ffffff",
              border: "0.3cqw solid rgba(0,0,0,.35)",
              borderRadius: "1.2cqw",
            }}
          >
            {brandUrl ? (
              <OutlinedImage
                src={brandUrl}
                filter={getOutlineFilter("#000000", 0, "solid", "#666666")}
                nominal={64}
                alt={isStar ? "estrela" : char}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="object-contain pointer-events-none"
                style={{ width: "85%", height: "85%" }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SupertBar({ progress, flash, supertCycle, supertCount, repeatLetter, cardNumbers, shimmer }) {
  const letters = progress.filter((p) => p !== "*");
  const stars = progress.filter((p) => p === "*").length;
  const slots = SUPERT.map((L) => (letters.includes(L) ? L : null));
  let starsLeft = stars;
  const allDisplay = slots.map((s, i) => {
    if (s) return { type: "letter", char: s, originalIdx: i };
    if (starsLeft > 0) { starsLeft -= 1; return { type: "star", char: "★", originalIdx: i }; }
    return { type: "empty", originalIdx: i };
  });
  const display = allDisplay.filter((d) => d.type !== "empty");
  if (display.length === 0) return null;
  const allFilled = display.length === 6;
  const repeatIdx = repeatLetter
    ? display.findIndex((d) => d.type !== "empty" && d.char === repeatLetter)
    : -1;
  // Cascade overlap geometry: 6 cards stacked left→right; each new card hides
  // ONLY the A-J grid (left col, 38% of width) and the ADHIA tabs of the next
  // card behind the previous card's right edge. The white letter/star panel
  // (right portion) of EVERY card stays fully visible.
  // Step = 0.62 * cardWidth (so each new card starts at the previous card's
  // letter-panel left edge — no overlap on the letter area).
  // Total width when 6 cards are visible: 1 + 5 × 0.62 = 4.1 card-widths.
  const CARDS_TOTAL_UNITS = 4.1;             // 1 + 5*0.62
  // Scale factor keeps a small right-side margin if < 1. With 1.0 the 6 cards
  // span the full bar width; bar height ≈ 17% of bar width.
  const CARD_SCALE = 1.0;
  const cardWidthPct = (100 / CARDS_TOTAL_UNITS) * CARD_SCALE;  // ≈ 24.4%
  const stepPct = cardWidthPct * 0.62;                          // ≈ 15.1%
  return (
    <div className="bg-white rounded-xl px-1.5 py-2 shadow border-2 border-white shrink-0" data-testid="supert-bar">
      {supertCount > 1 && (
        <div className="text-center text-[10px] md:text-xs font-[Fredoka] font-bold text-slate-600 mb-1 tracking-wide" data-testid="supert-cycle-label">
          SUPERT {Math.min(supertCycle + 1, supertCount)} / {supertCount}
        </div>
      )}
      {/* Cascade row: absolute cards positioned at SUPERT slots. Container
          uses `aspectRatio` to size itself so cards keep exact 1.4:1 landscape. */}
      <div className="relative" style={{ aspectRatio: `${100 * 1.4 / cardWidthPct} / 1` }}>
        {display.map((item, i) => {
          const isStar = item.type === "star";
          // Each won letter goes to its FIXED SUPERT position (S=0…T=5).
          // Stars take the first empty SUPERT slot (enforced when building
          // `display`). This ensures the bar visually forms SUPERT.
          const left = `${item.originalIdx * stepPct}%`;
          // z-index: leftmost SUPERT position in front of next — this keeps
          // the big letter/star panel of EVERY card fully visible. The base
          // card NEVER changes z (the repeated-letter effect renders as an
          // independent overlay below, so the cascade order is never broken).
          const z = 6 - item.originalIdx;
          return (
            <motion.div
              key={item.originalIdx}
              data-testid={`supert-slot-${item.originalIdx}`}
              className="absolute top-0 bottom-0"
              style={{
                left,
                width: `${cardWidthPct}%`,
                zIndex: z,
              }}
              initial={{ opacity: 0, x: `-${stepPct}%`, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <motion.div
                animate={
                  flash
                    ? allFilled
                      ? {
                          scale: [1, 1.18, 0.96, 1.14, 1],
                          rotate: [0, -2, 2, -1, 0],
                          filter: [
                            "drop-shadow(0 1px 0 rgba(0,0,0,0.25))",
                            "drop-shadow(0 0 18px rgba(251,191,36,1))",
                            "drop-shadow(0 0 10px rgba(251,191,36,0.95))",
                            "drop-shadow(0 0 22px rgba(255,255,255,0.9))",
                            "drop-shadow(0 1px 0 rgba(0,0,0,0.25))",
                          ],
                        }
                      : {
                          scale: [1, 1.12, 1],
                          filter: [
                            "drop-shadow(0 1px 0 rgba(0,0,0,0.25))",
                            "drop-shadow(0 0 14px rgba(251,191,36,0.95))",
                            "drop-shadow(0 1px 0 rgba(0,0,0,0.25))",
                          ],
                        }
                    : {}
                }
                transition={
                  flash
                    ? { duration: allFilled ? 1.0 : 0.7, repeat: allFilled ? 5 : 4, delay: i * 0.08 }
                    : {}
                }
                className="relative w-full h-full"
                style={{ transformOrigin: "center center" }}
              >
                <MiniRouletteCard
                  char={isStar ? "*" : item.char}
                  isFilled={true}
                  isStar={isStar}
                  cardNumbers={cardNumbers}
                  shimmer={shimmer}
                />
              </motion.div>

              <AnimatePresence>
                {flash && allFilled && (
                  <motion.div
                    key={`burst-${item.originalIdx}`}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: [0, 1.4, 1.8], opacity: [0, 0.95, 0] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.4, repeat: 4, delay: i * 0.12, ease: "easeOut" }}
                    className="absolute inset-0 z-20 pointer-events-none rounded-md"
                    style={{
                      background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.95) 0%, rgba(251,191,36,0.6) 35%, rgba(251,191,36,0) 70%)",
                      mixBlendMode: "screen",
                    }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {/* Repeated-letter overlay — an INDEPENDENT copy of the repeated card
            that appears ON TOP of the whole cascade at the slot's position,
            glows briefly and fades out, leaving only the card that was
            already there. Rendered outside the slots so it never alters the
            cascade z-order (base cards keep their letter panels visible).
            Key is STABLE per letter — no retrigger on 60Hz parent renders. */}
        <AnimatePresence>
          {repeatIdx >= 0 && repeatLetter && (
            <motion.div
              key={`rep-${repeatLetter}`}
              data-testid={`supert-repeat-${display[repeatIdx].originalIdx}`}
              initial={{ opacity: 0, scale: 1.25 }}
              animate={{ opacity: [0, 1, 1, 0], scale: [1.25, 1.12, 1.04, 1] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.0, times: [0, 0.2, 0.75, 1], ease: "easeInOut" }}
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                left: `${display[repeatIdx].originalIdx * stepPct}%`,
                width: `${cardWidthPct}%`,
                zIndex: 1000,
                filter: "drop-shadow(0 0 14px rgba(251,191,36,0.95))",
                transformOrigin: "center center",
              }}
            >
              <MiniRouletteCard
                char={repeatLetter === "*" ? "*" : repeatLetter}
                isFilled={true}
                isStar={repeatLetter === "*"}
                cardNumbers={cardNumbers}
                shimmer={false}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// -------------- Activity / profile / modal --------------
export function ActivitySummary({ history }) {
  const attempts = (history?.attempts || []).filter((a) => a.status !== "in_progress");
  const cred = history?.credential || {};
  return (
    <div className="mt-4 text-left bg-slate-50 rounded-xl p-3 border border-slate-200" data-testid="activity-summary">
      <div className="font-[Fredoka] font-bold text-slate-900 text-sm mb-2 text-center">Resumo de atividade</div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
        <div className="bg-emerald-100 rounded-md py-1.5 px-2">
          <div className="text-emerald-700 font-bold">Vitórias</div>
          <div className="text-emerald-900 font-mono font-bold text-base" data-testid="summary-victories">{cred.victories ?? 0}</div>
        </div>
        <div className="bg-rose-100 rounded-md py-1.5 px-2">
          <div className="text-rose-700 font-bold">Derrotas</div>
          <div className="text-rose-900 font-mono font-bold text-base" data-testid="summary-defeats">{cred.defeats ?? 0}</div>
        </div>
        <div className="bg-sky-100 rounded-md py-1.5 px-2">
          <div className="text-sky-700 font-bold">Melhor</div>
          <div className="text-sky-900 font-mono font-bold text-base" data-testid="summary-best">{cred.best_time ? formatTime(cred.best_time) : "—"}</div>
        </div>
      </div>
      {attempts.length > 0 && (
        <div className="max-h-32 overflow-y-auto border-t border-slate-200 pt-1 mt-1">
          {attempts.slice(0, 10).map((a, i) => (
            <div key={a.id || a.session_id || `attempt-${i}-${a.time_seconds || 0}`} className="flex justify-between text-[11px] py-0.5 border-b border-slate-100 last:border-0">
              <span className={a.status === "won" ? "text-emerald-700 font-bold" : "text-rose-600 font-bold"}>
                {a.status === "won" ? "✓ Vitória" : a.status === "lost" ? "✗ Derrota" : a.status}
              </span>
              <span className="font-mono text-slate-700">{a.time_seconds != null ? formatTime(a.time_seconds) : "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProfileCard({ name, nationality, school, avatarCfg, gameName, onEditAvatar, xp = 0, level = null, bestTime = null, levelLabel = null }) {
  // Each "rank" = 100 XP. Progress = (xp % 100) / 100. Rank starts at 1.
  const XP_PER_RANK = 100;
  const rank = Math.max(1, Math.floor((xp || 0) / XP_PER_RANK) + 1);
  const progress = Math.max(0, Math.min(1, ((xp || 0) % XP_PER_RANK) / XP_PER_RANK));
  // SVG geometry
  const SIZE = 150;          // overall ring size (a touch larger than the avatar)
  const STROKE = 6;
  const R = (SIZE - STROKE) / 2;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - progress);
  // Format best time: "0:32.45" if > 60s, otherwise "32.45 s"
  const fmtTime = (t) => {
    if (t == null || !Number.isFinite(t) || t <= 0) return null;
    if (t >= 60) {
      const mm = Math.floor(t / 60);
      const ss = (t - mm * 60).toFixed(1).padStart(4, "0");
      return `${mm}:${ss}`;
    }
    return `${t.toFixed(1)}s`;
  };
  const bestTimeLabel = fmtTime(bestTime);

  // ── Personal Record flash ──────────────────────────────────────────────
  // Trigger a pulse + golden glow animation when bestTime decreases (new PR).
  // We compare against the previous render's value held in a ref.
  const prevBestRef = React.useRef(bestTime);
  const [prFlash, setPrFlash] = React.useState(false);
  React.useEffect(() => {
    const prev = prevBestRef.current;
    if (
      bestTime != null && Number.isFinite(bestTime) && bestTime > 0 &&
      prev != null && Number.isFinite(prev) && prev > 0 &&
      bestTime < prev
    ) {
      setPrFlash(true);
      const id = setTimeout(() => setPrFlash(false), 2800);
      prevBestRef.current = bestTime;
      return () => clearTimeout(id);
    }
    prevBestRef.current = bestTime;
  }, [bestTime]);
  return (
    <div
      className="relative bg-white rounded-2xl shadow-md overflow-visible mt-auto"
      data-testid="player-profile"
    >
      {/* Top section — sky/cyan gradient with game name + country */}
      <div className="relative rounded-t-2xl bg-gradient-to-b from-sky-100 to-cyan-50 px-3 pt-2 pb-3 pr-[140px]">
        <FitText
          as="div"
          maxPx={11}
          minPx={7}
          className="uppercase tracking-[0.18em] font-bold text-slate-500 leading-tight"
          data-testid="profile-game"
          title={gameName}
        >
          {gameName}
        </FitText>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-700 mt-1.5">
          {flagUrl(nationality) && (
            <img
              src={flagUrl(nationality)}
              alt={nationality}
              className="w-5 h-5 rounded-full object-cover ring-1 ring-white shadow-sm"
            />
          )}
          <span className="truncate">{nationalityLabel(nationality)}</span>
        </div>
      </div>

      {/* Bottom section — white, with name + school */}
      <div className="px-3 py-3 pr-[140px] rounded-b-2xl">
        <div
          className="font-[Fredoka] font-extrabold text-slate-900 leading-tight truncate text-lg md:text-xl"
          data-testid="profile-name"
          title={name}
        >
          {name}
        </div>
        {school ? (
          <div className="text-[11px] text-slate-500 truncate mt-1" data-testid="profile-school" title={school}>
            {school}
          </div>
        ) : null}
        {levelLabel ? (
          // Player's own level/escalão. Uses FitText so long labels like
          // "3.º + 4.º ANO (Nível 1)" always fit on a single line — the
          // font size shrinks progressively instead of being clipped.
          <FitText
            as="div"
            maxPx={11}
            minPx={7}
            className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-extrabold uppercase tracking-wider"
            data-testid="profile-level"
            title={levelLabel}
          >
            {levelLabel}
          </FitText>
        ) : null}
      </div>

      {/* Avatar — overlapping the card on the right with yellow halo + XP ring */}
      <button
        onClick={onEditAvatar}
        data-testid="profile-avatar"
        title={`Editar avatar · Nível ${level ?? rank} · ${xp || 0} XP`}
        className="absolute -top-5 -right-4 z-10 group"
        aria-label="Editar avatar"
        style={{ width: SIZE, height: SIZE }}
      >
        {/* SVG progress ring (under the halo) */}
        <svg
          className="absolute inset-0 pointer-events-none"
          width={SIZE}
          height={SIZE}
          data-testid="profile-xp-ring"
        >
          <defs>
            <linearGradient id="supert-xp-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#fde047" />
              <stop offset="55%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#fb923c" />
            </linearGradient>
          </defs>
          {/* Track */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke="rgba(15,23,42,0.08)"
            strokeWidth={STROKE}
          />
          {/* Filled arc — starts at top (12 o'clock), grows clockwise */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke="url(#supert-xp-grad)"
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            style={{ transition: "stroke-dashoffset 600ms cubic-bezier(0.4,0,0.2,1)" }}
            data-testid="profile-xp-ring-fill"
          />
        </svg>

        {/* Yellow soft halo behind avatar */}
        <div
          className="absolute"
          style={{
            top: STROKE + 2,
            left: STROKE + 2,
            width: SIZE - 2 * (STROKE + 2),
            height: SIZE - 2 * (STROKE + 2),
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 35%, rgba(253,224,71,0.95) 0%, rgba(250,204,21,0.55) 65%, rgba(250,204,21,0) 100%)",
           
          }}
        />

        {/* Sky-blue inner circle with the avatar */}
        <div
          className="absolute overflow-hidden grid place-items-center bg-sky-200 ring-2 ring-white shadow"
          style={{
            top: STROKE + 6,
            left: STROKE + 6,
            width: SIZE - 2 * (STROKE + 6),
            height: SIZE - 2 * (STROKE + 6),
            borderRadius: "50%",
          }}
        >
          <AvatarSVG cfg={avatarCfg} size={SIZE - 2 * (STROKE + 6)} />
        </div>

        {/* Best-time badge — shows the player's overall best successfully-completed
            time across all games. Falls back to "Rank -" when no time exists yet.
            When a new personal record is detected, a glowing pulse animation plays. */}
        <span
          className={`absolute z-20 inline-flex items-center justify-center font-[Fredoka] font-extrabold text-[11px] text-amber-950 bg-gradient-to-b from-yellow-300 to-amber-400 ring-2 ring-white shadow-md rounded-full px-2 py-0.5 leading-none whitespace-nowrap ${prFlash ? "supert-pr-flash" : ""}`}
          style={{ bottom: -4, right: 6, minWidth: 36 }}
          data-testid="profile-best-time-badge"
          title={
            prFlash
              ? `Novo recorde pessoal: ${bestTimeLabel}!`
              : (bestTimeLabel ? `Melhor tempo: ${bestTimeLabel}` : "Sem tempos registados ainda")
          }
        >
          {bestTimeLabel || "Rank -"}
        </span>
        {/* "NOVO RECORDE" floating label + sparkle particles bursting from the badge */}
        {prFlash && (
          <>
            <span
              aria-hidden
              className="absolute z-30 inline-flex items-center font-[Fredoka] font-extrabold text-[10px] text-amber-700 bg-white/95 ring-2 ring-amber-300 shadow-md rounded-full px-2 py-0.5 whitespace-nowrap supert-pr-label"
              style={{ bottom: 18, right: -8 }}
              data-testid="profile-pr-label"
            >
              ★ NOVO RECORDE
            </span>
            <span aria-hidden className="absolute pointer-events-none" style={{ bottom: 6, right: 22, width: 0, height: 0, zIndex: 25 }} data-testid="profile-pr-sparkles">
              {Array.from({ length: 7 }).map((_, i) => {
                // Spread across an upper-half arc (-180° to 0°), with a tiny random jitter.
                const angle = (-Math.PI) + (i / 6) * Math.PI + (Math.random() - 0.5) * 0.25;
                const dist = 46 + Math.random() * 22;
                const tx = Math.cos(angle) * dist;
                const ty = Math.sin(angle) * dist;
                const rot = `${Math.round((Math.random() * 540) - 270)}deg`;
                const delay = `${(i * 60 + Math.random() * 80)}ms`;
                return (
                  <span
                    key={`sparkle-${i}`}
                    className="supert-pr-sparkle"
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      fontSize: 14 + Math.random() * 8,
                      lineHeight: 1,
                      color: ["#fbbf24", "#fef3c7", "#fde047", "#f59e0b", "#fff"][i % 5],
                      textShadow: "0 0 6px rgba(251,191,36,0.85)",
                      "--tx": `${tx}px`,
                      "--ty": `${ty}px`,
                      "--rot": rot,
                      animationDelay: delay,
                    }}
                  >
                    ✨
                  </span>
                );
              })}
            </span>
          </>
        )}

        {/* Edit pencil on hover */}
        <span className="absolute top-1 right-1 w-6 h-6 rounded-full bg-sky-500 text-white grid place-items-center text-xs shadow ring-2 ring-white opacity-0 group-hover:opacity-100 transition-opacity">
          ✎
        </span>
      </button>
    </div>
  );
}

export function Modal({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
    >
      <motion.div
        initial={{ scale: 0.7, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.7, opacity: 0 }}
        className="bg-white rounded-3xl p-8 md:p-10 text-center max-w-md w-full shadow-2xl"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
